import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { EM_DASH } from '~/common/constants/unicode';
import { Box } from '~/eds/src/components/Box/Box';
import { Select } from '~/eds/src/components/Select/Select';

import { extractSelectValue } from '../../helpers';
import { useAsyncFieldValues } from '../../hooks/useAsyncFieldValues';
import { BaseFieldProps } from './fieldTypes';

interface SelectOption {
  value: string | number;
  label: string;
}

export const SingleArrayField = ({
  field,
  editMode,
  editData,
  onEditDataChange,
  isWssField,
}: BaseFieldProps) => {
  const intl = useIntl();
  const presentValue = editData[field.id];

  const {
    asyncOptions,
    handleSearch,
    handleAddOption,
    shouldLoadOnMount,
    isPartiesField,
  } = useAsyncFieldValues({
    fieldId: field.id,
    className: field.class_name,
    allowNewOptions: field.allow_new_options,
    currentValue: presentValue,
    onChange: onEditDataChange,
  });

  useEffect(() => {
    if (editMode && shouldLoadOnMount) {
      handleSearch('');
    }
  }, [editMode, shouldLoadOnMount, handleSearch]);

  if (editMode) {
    const currentValue = extractSelectValue(presentValue);

    return (
      <Box flex="1">
        <Select
          name={field.name}
          value={currentValue}
          options={asyncOptions.data}
          isLoading={asyncOptions.isFetching}
          onSearch={handleSearch}
          onChange={(newValue: string | number | null) => {
            if (!newValue) {
              onEditDataChange(field.id, null);
              return;
            }
            const option = asyncOptions.data.find(
              (opt: SelectOption) => opt.value === newValue,
            );
            onEditDataChange(field.id, {
              value: newValue,
              label: option?.label || String(newValue),
            });
          }}
          onAddOption={handleAddOption}
          disabled={!field.is_editable || isWssField(field)}
          isMulti={false}
          isClearable={field.is_nullable}
          isSearchable
          placeholder={intl.formatMessage(
            {
              id: 'DocumentOverviewPanel.searchFieldName',
              defaultMessage: 'Search for {fieldName}...',
              description:
                'Placeholder text for search field. Example: fieldName = "Contract Type"',
            },
            { fieldName: field.name },
          )}
          noOptionsMessage={
            isPartiesField
              ? intl.formatMessage({
                  id: 'DocumentOverviewPanel.typeAtLeast3Letters',
                  defaultMessage: 'Type in at least 3 letters...',
                  description:
                    'Message for parties fields requiring minimum input length',
                })
              : intl.formatMessage(
                  {
                    id: 'DocumentOverviewPanel.noFieldNameFound',
                    defaultMessage: 'No {fieldName} found',
                    description:
                      'Message when no search results found. Example: fieldName = "Contract Type"',
                  },
                  { fieldName: field.name },
                )
          }
        />
      </Box>
    );
  }

  return field.value && !Array.isArray(field.value)
    ? field.value.display_value
    : EM_DASH;
};
